import type { PracticalInfoLayout } from '@/types/practicalInfo'

export type RaceType =
  | 'road'
  | 'trail'
  | 'track'
  | 'duathlon'
  | 'triathlon'
  | 'cycling'
  | 'virtual'
  | 'other'

export type EventFontCategory =
  | 'sans-serif'
  | 'serif'
  | 'display'
  | 'monospace'
  | 'handwriting'

export type EventFontSlug =
  // Sans-serif
  | 'inter'
  | 'roboto'
  | 'open-sans'
  | 'montserrat'
  | 'poppins'
  | 'lato'
  | 'nunito'
  | 'work-sans'
  | 'space-grotesk'
  | 'dm-sans'
  | 'source-sans-3'
  | 'figtree'
  | 'plus-jakarta-sans'
  // Serif
  | 'playfair-display'
  | 'fraunces'
  | 'merriweather'
  | 'libre-baskerville'
  | 'cormorant-garamond'
  | 'eb-garamond'
  // Display
  | 'oswald'
  | 'raleway'
  | 'bebas-neue'
  | 'barlow-condensed'
  | 'archivo-black'
  | 'anton'
  // Monospace
  | 'space-mono'
  | 'roboto-mono'
  | 'jetbrains-mono'
  | 'fira-code'
  // Handwriting / Script
  | 'pacifico'
  | 'dancing-script'
  | 'caveat'
  | 'permanent-marker'
  | 'satisfy'
  | 'grand-hotel'

export const EVENT_FONTS: ReadonlyArray<{
  slug: EventFontSlug
  family: string
  category: EventFontCategory
}> = [
  // Sans-serif
  { slug: 'inter',             family: 'Inter',             category: 'sans-serif' },
  { slug: 'roboto',            family: 'Roboto',            category: 'sans-serif' },
  { slug: 'open-sans',         family: 'Open Sans',         category: 'sans-serif' },
  { slug: 'montserrat',        family: 'Montserrat',        category: 'sans-serif' },
  { slug: 'poppins',           family: 'Poppins',           category: 'sans-serif' },
  { slug: 'lato',              family: 'Lato',              category: 'sans-serif' },
  { slug: 'nunito',            family: 'Nunito',            category: 'sans-serif' },
  { slug: 'work-sans',         family: 'Work Sans',         category: 'sans-serif' },
  { slug: 'space-grotesk',     family: 'Space Grotesk',     category: 'sans-serif' },
  { slug: 'dm-sans',           family: 'DM Sans',           category: 'sans-serif' },
  { slug: 'source-sans-3',     family: 'Source Sans 3',     category: 'sans-serif' },
  { slug: 'figtree',           family: 'Figtree',           category: 'sans-serif' },
  { slug: 'plus-jakarta-sans', family: 'Plus Jakarta Sans', category: 'sans-serif' },
  // Serif
  { slug: 'playfair-display',  family: 'Playfair Display',  category: 'serif' },
  { slug: 'fraunces',          family: 'Fraunces',          category: 'serif' },
  { slug: 'merriweather',      family: 'Merriweather',      category: 'serif' },
  { slug: 'libre-baskerville', family: 'Libre Baskerville', category: 'serif' },
  { slug: 'cormorant-garamond',family: 'Cormorant Garamond',category: 'serif' },
  { slug: 'eb-garamond',       family: 'EB Garamond',       category: 'serif' },
  // Display
  { slug: 'oswald',            family: 'Oswald',            category: 'display' },
  { slug: 'raleway',           family: 'Raleway',           category: 'display' },
  { slug: 'bebas-neue',        family: 'Bebas Neue',        category: 'display' },
  { slug: 'barlow-condensed',  family: 'Barlow Condensed',  category: 'display' },
  { slug: 'archivo-black',     family: 'Archivo Black',     category: 'display' },
  { slug: 'anton',             family: 'Anton',             category: 'display' },
  // Monospace
  { slug: 'space-mono',        family: 'Space Mono',        category: 'monospace' },
  { slug: 'roboto-mono',       family: 'Roboto Mono',       category: 'monospace' },
  { slug: 'jetbrains-mono',    family: 'JetBrains Mono',    category: 'monospace' },
  { slug: 'fira-code',         family: 'Fira Code',         category: 'monospace' },
  // Handwriting / Script
  { slug: 'pacifico',          family: 'Pacifico',          category: 'handwriting' },
  { slug: 'dancing-script',    family: 'Dancing Script',    category: 'handwriting' },
  { slug: 'caveat',            family: 'Caveat',            category: 'handwriting' },
  { slug: 'permanent-marker',  family: 'Permanent Marker',  category: 'handwriting' },
  { slug: 'satisfy',           family: 'Satisfy',           category: 'handwriting' },
  { slug: 'grand-hotel',       family: 'Grand Hotel',       category: 'handwriting' },
] as const

export type EventLanguageCode = 'nl' | 'fr' | 'en'

/**
 * The locales the public event website ships message bundles for
 * (apps/event-site/src/i18n/locales). Must stay in sync with App\Enums\EventLanguage.
 */
export const EVENT_LANGUAGES: ReadonlyArray<{ code: EventLanguageCode }> = [
  { code: 'nl' },
  { code: 'fr' },
  { code: 'en' },
] as const

export type EventTemplateSlug = 'classic'

export type EventThemeSlug =
  | 'granite'
  | 'alpine'
  | 'desert'
  | 'coastal'
  | 'midnight'
  | 'meadow'

export const EVENT_TEMPLATES: ReadonlyArray<{ slug: EventTemplateSlug }> = [
  { slug: 'classic' },
] as const

export const EVENT_THEMES: ReadonlyArray<{
  slug: EventThemeSlug
  swatches: [string, string, string]
}> = [
  { slug: 'granite',  swatches: ['#E63946', '#0F1419', '#F4F5F7'] },
  { slug: 'alpine',   swatches: ['#F4A261', '#0E1814', '#172420'] },
  { slug: 'desert',   swatches: ['#B33A1F', '#3B1F0E', '#FAF3EA'] },
  { slug: 'coastal',  swatches: ['#0E7C86', '#0E2230', '#F1F6F9'] },
  { slug: 'midnight', swatches: ['#FFD23F', '#0A0E1A', '#141A2B'] },
  { slug: 'meadow',   swatches: ['#3F8F3D', '#1A2913', '#F7FAF3'] },
] as const

export interface Event {
  id: number
  organisation_id: number
  name: string
  tagline: string | null
  description: string | null
  slug: string
  facebook_page: string | null
  instagram_page: string | null
  external_website_url: string | null
  mail_bcc: string | null
  contact_email: string | null
  maintenance: boolean
  website_enabled: boolean
  youtube_url: string | null
  logo_url: string | null
  template: EventTemplateSlug
  theme: EventThemeSlug
  font_heading: EventFontSlug
  font_body: EventFontSlug
  language: EventLanguageCode
  editions?: Edition[]
  active_edition?: Edition | null
  created_at: string
  updated_at: string
}

/**
 * Deletability verdict for a venue, decided entirely by the API.
 * The SPA renders what it is told and never infers this client-side.
 */
export type VenueDeletionStatus = 'deletable' | 'deletable_with_warning' | 'blocked'

/**
 * OPTIONAL BY CONTRACT. `VenueResource` omits the whole `deletion` key when the
 * edition-count aggregates were not loaded, rather than emitting a guessed
 * `deletable / 0 / 0` verdict. So the block is present on the organizer venue
 * list and show endpoints, and ABSENT wherever a venue is serialised as a nested
 * relation without aggregates (for example `edition.venue`).
 *
 * Absent means UNKNOWN, never "safe to delete". Any consumer must treat a
 * missing block as "no verdict" and withhold the delete affordance entirely.
 * Typing it as always present would be the same type-level lie that caused the
 * `Edition.venue_id` defect.
 */

export interface VenueDeletion {
  status: VenueDeletionStatus
  active_edition_count: number
  inactive_edition_count: number
}

/** How many editions and races read their times in this venue's time zone. */
export interface VenueUsage {
  edition_count: number
  race_count: number
}

export interface Venue {
  id: number
  organisation_id: number
  name: string | null
  street: string | null
  city: string | null
  postal_code: string | null
  country: string | null
  latitude: string | null
  longitude: string | null
  // IANA zone of the place; race and programme times at this venue are read in it.
  timezone: string
  // Optional: see the VenueDeletion doc block. Absent = verdict unknown.
  deletion?: VenueDeletion
  // Organiser-only; absent on nested venues (for example edition.venue). Absent = unknown.
  usage?: VenueUsage
  created_at: string
  updated_at: string
}

export interface StoreVenuePayload {
  name?: string | null
  street?: string | null
  city?: string | null
  postal_code?: string | null
  country?: string | null
  // The SPA always sends it; the API derives it when omitted on store.
  timezone?: string
}

export type UpdateVenuePayload = StoreVenuePayload

export interface Edition {
  id: number
  event_id: number
  name: string
  slug: string
  active: boolean
  start_date: string
  end_date: string
  // The API never returns a flat `venue_id` on an edition - only the nested
  // `venue` relation. Read `venue?.id ?? null` when a venue id is needed.
  venue?: Venue | null
  participant_count: number
  total_revenue: string
  volunteers_enabled: boolean
  practical_info_layout: PracticalInfoLayout
  races?: Race[]
  race_rules: string | null
  terms_and_conditions: string | null
  created_at: string
  updated_at: string
}

/**
 * The venue triple carried by every race payload.
 *
 * `start_venue` is already RESOLVED by the backend: it is the race's own venue
 * when it has one, otherwise the edition's venue, otherwise null. Never
 * re-implement that fallback in the frontend.
 */
export interface RaceVenues {
  start_venue: PublicVenue | null
  start_venue_inherited: boolean
  end_venue: PublicVenue | null
}

export interface Race {
  id: number
  edition_id: number
  name: string
  slug: string
  type: RaceType
  base_price: string
  distance: string
  elevation: number | null
  // Raw foreign keys - only the organizer RaceResource exposes these. Prefill
  // the race form from these, never from the resolved `start_venue`.
  start_venue_id: number | null
  end_venue_id: number | null
  start_venue: PublicVenue | null
  start_venue_inherited: boolean
  end_venue: PublicVenue | null
  start_time: string
  cutoff: number | null
  max_participants: number | null
  allow_waiting_list: boolean
  payment_deadline_hours: number | null
  registrations_closing_time: string | null
  registrations_open: boolean
  sort_order: number
  cover_image_url: string | null
  participant_count: number
  total_revenue: string
  // Backend-computed pricing summary; render as is, never derive it in Vue.
  pricing: RacePricing
  price_tiers?: RacePriceTier[]
  wizard_form?: import('@/types/wizard-forms').WizardForm | null
  created_at: string
  updated_at: string
}

export interface StoreEventPayload {
  name: string
  tagline?: string | null
  description?: string | null
  facebook_page?: string | null
  instagram_page?: string | null
  external_website_url?: string | null
  mail_bcc?: string | null
  contact_email?: string | null
  maintenance?: boolean
  website_enabled?: boolean
  youtube_url?: string | null
  template?: EventTemplateSlug
  theme?: EventThemeSlug
  font_heading?: EventFontSlug
  font_body?: EventFontSlug
  language?: EventLanguageCode
}

export type UpdateEventPayload = StoreEventPayload

export interface StoreEditionPayload {
  name: string
  start_date: string
  end_date: string
  venue_id?: number | null
  active?: boolean
}

export type UpdateEditionPayload = Omit<StoreEditionPayload, 'active'> & {
  race_rules?: string | null
  terms_and_conditions?: string | null
}

export interface RacePricing {
  currency: string
  current_price_cents: number
  standard_price_cents: number
  has_tiers: boolean
  current_tier: { name: string; ends_at: string | null; remaining_spots: number | null } | null
  next_price_change: {
    price_cents: number
    tier_name: string | null
    at: string | null
    direction: 'increase' | 'decrease'
  } | null
}

export type RacePriceTierStatus = 'upcoming' | 'active' | 'ended' | 'sold_out'

export interface RacePriceTier {
  id: number
  name: string
  price: string
  ends_at: string | null
  max_registrations: number | null
  position: number
  registrations_count: number
  remaining_spots: number | null
  status: RacePriceTierStatus
  has_registrations: boolean
}

export interface StoreRacePriceTierPayload {
  name: string
  price: number
  ends_at: string | null
  max_registrations: number | null
}

// Create page only: an unsaved tier. `key` is a client-only id for v-for and is stripped before sending.
export interface DraftRacePriceTier {
  key: string
  name: string
  price: string
  ends_at: string | null
  max_registrations: number | null
}

export interface StoreRacePayload {
  name: string
  type: RaceType
  base_price: number
  distance: number
  elevation?: number | null
  // Send `null` explicitly to clear a venue: the backend rules are
  // `sometimes|nullable`, so omitting the key leaves the stored value untouched.
  start_venue_id?: number | null
  end_venue_id?: number | null
  start_time: string
  cutoff?: number | null
  max_participants?: number | null
  allow_waiting_list?: boolean
  payment_deadline_hours?: number | null
  registrations_closing_time?: string | null
  registrations_open?: boolean
  sort_order?: number
  // Create only: tiers are stored atomically with the race. Ignored on update.
  price_tiers?: StoreRacePriceTierPayload[]
}

export type UpdateRacePayload = StoreRacePayload

// Public event races page types
export interface PublicEventRace {
  id: number
  name: string
  slug: string
  type: RaceType
  distance: string
  elevation: number | null
  start_venue: PublicVenue | null
  start_venue_inherited: boolean
  start_time: string
  base_price: string
  pricing: RacePricing
  max_participants: number | null
  registrations_count: number
  is_closed: boolean
  // Absent on older API versions; 'not_yet_open' = paid race awaiting payout setup.
  registration_availability?: RaceRegistrationAvailability
  registrations_closing_time: string | null
  is_waiting_list_active: boolean
}

export type RaceRegistrationAvailability = 'open' | 'not_yet_open' | 'closed'

export interface PublicVenue {
  name: string | null
  street: string | null
  city: string | null
  postal_code: string | null
  country: string | null
  latitude: string | null
  longitude: string | null
  timezone: string
}

export interface PublicEventData {
  event: {
    name: string
    slug: string
    logo_url: string | null
    website_url: string | null
  }
  edition: {
    name: string
    start_date: string
    end_date: string
    venue: PublicVenue | null
    race_rules: string | null
    terms_and_conditions: string | null
  }
  organiser: {
    slug: string | null
    name: string | null
    email: string | null
    website: string | null
    logo_url: string | null
  }
  races: PublicEventRace[]
}
